import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "./MemberDetailsModal.css";
import MembershipHistoryModal from "./MembershipHistoryModal";
import MembershipDegreesModal from "./MembershipDegreesModal";
import OfficerRoleModal from "./OfficerHistoryModal";
import MemberProfileModal from "./MemberProfileModal";
import Select from "react-select";

function MemberDetailsModal({ onClose, onSave, initialMemberId }) {
    const membershipCategoryOptions = [
        { value: "current", label: "Current member" },
        { value: "former", label: "Not a member anymore" },
    ];
    const currentTitleOptions = [
        { value: "Bro.", label: "Bro." },
        { value: "W. Bro.", label: "W. Bro." },
        { value: "V. Wor. Bro.", label: "V. Wor. Bro." },
        { value: "Rt. Wor. Bro.", label: "Rt. Wor. Bro." },
        { value: "M. Wor. Bro.", label: "M. Wor. Bro." },
    ];

    const [members, setMembers] = useState([]);
    const [memberRows, setMemberRows] = useState([]);
    const [memberCount, setMemberCount] = useState(0);
    const [countries, setCountries] = useState([]);
    const [lodges, setLodges] = useState([]);
    const [membershipTypes, setMembershipTypes] = useState([]);
    const [membershipStatuses, setMembershipStatuses] = useState([]);
    const [memberFilters, setMemberFilters] = useState({
        search: "",
        lodgeId: "",
        statusId: "",
        memberCategory: "",
        activeOnly: false,
    });
    const [formData, setFormData] = useState({});
    const [selectedMember, setSelectedMember] = useState(null);
    const [memberProfile, setMemberProfile] = useState(null);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [errors, setErrors] = useState({});
    const [currentModal, setCurrentModal] = useState(null);
    const [activeTab, setActiveTab] = useState("overview");
    const [editingMembershipId, setEditingMembershipId] = useState(null);
    const [membershipEditData, setMembershipEditData] = useState({});
    const [showLodgeRollView, setShowLodgeRollView] = useState(false);
    const [memberQuickFilter, setMemberQuickFilter] = useState(null);
    const [photoUploading, setPhotoUploading] = useState(false);
    const photoInputRef = useRef(null);

    useEffect(() => {
        fetchMemberCount();
        fetchCountries();
        fetchFilterOptions();
    }, []);

    useEffect(() => {
        if (!initialMemberId || !members.length || selectedMember?.id === initialMemberId) return;
        handleMemberSelect({ value: initialMemberId });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialMemberId, members, selectedMember?.id]);

    const fetchMembers = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (memberFilters.search.trim()) params.set("search", memberFilters.search.trim());
            if (memberFilters.lodgeId) params.set("lodgeId", memberFilters.lodgeId);
            if (memberFilters.statusId) params.set("statusId", memberFilters.statusId);
            if (memberFilters.memberCategory) params.set("memberCategory", memberFilters.memberCategory);
            if (memberFilters.activeOnly) params.set("activeOnly", "true");
            const res = await axios.get(`/api/resources/members${params.toString() ? `?${params}` : ""}`);
            setMemberRows(res.data || []);
            const memberOptions = res.data.map((member) => ({
                value: member.id,
                label: [
                    `${member.first_name || ""} ${member.last_name || ""}`.trim() || member.id,
                    member.current_lodge_names || member.current_lodge_name,
                    member.current_membership_status,
                ].filter(Boolean).join(" · "),
                member,
                photoPath: member.profile_photo_path || "",
            }));
            setMembers([{ value: "new", label: "Add New Member" }, ...memberOptions]);
        } catch (error) {
            console.error("Error fetching members:", error);
        }
    }, [memberFilters]);

    useEffect(() => {
        fetchMembers();
    }, [fetchMembers]);

    const fetchMemberCount = async () => {
        try {
            const res = await axios.get("/api/resources/members/count");
            setMemberCount(res.data.count);
        } catch (error) {
            console.error("Error fetching member count:", error);
        }
    };

    const fetchCountries = async () => {
        try {
            const res = await axios.get("/api/resources/countries");
            setCountries(res.data);
        } catch (error) {
            console.error("Error fetching countries:", error);
        }
    };

    const fetchFilterOptions = async () => {
        try {
            const [lodgesResponse, typesResponse, statusesResponse] = await Promise.all([
                axios.get("/api/resources/lodges"),
                axios.get("/api/membership-types"),
                axios.get("/api/membership-statuses"),
            ]);
            setLodges(lodgesResponse.data || []);
            setMembershipTypes(typesResponse.data || []);
            setMembershipStatuses(statusesResponse.data || []);
        } catch (error) {
            console.error("Error fetching member filter options:", error);
        }
    };

    const formatStreamLabel = (value, fallbackText = "") => {
        const label = String(value || "").trim();
        const combined = `${label} ${fallbackText || ""}`.toLowerCase();
        if (/royal\s*arch|chapter|_ra\b/.test(combined)) return "Royal Arch";
        if (/knights?\s*mason|council/.test(combined)) return "Knight Masons";
        if (/templar|preceptory|conclave|malta/.test(combined)) return "Knights Templar";
        if (/craft|lodge/.test(combined)) return "Craft";
        if (!label) return "";
        return label
            .replace(/\bLodges\b/i, "")
            .replace(/\bChapters\b/i, "")
            .replace(/\bCouncils\b/i, "")
            .replace(/\bConclaves\b/i, "")
            .replace(/\bPreceptories\b/i, "")
            .replace(/\(Local(?: level)?\)/i, "")
            .replace(/\s+/g, " ")
            .trim();
    };
    const lodgeStreamLabel = (lodgeId) => {
        const lodge = lodges.find((item) => item.id === lodgeId);
        return formatStreamLabel(lodge?.lodge_type_description, lodge?.name);
    };
    const mainBodyLabel = (lodgeId) => {
        const streamLabel = lodgeStreamLabel(lodgeId);
        return streamLabel ? `Main ${streamLabel} body` : "Main body";
    };

    const updateMemberFilter = (field, value) => {
        setMemberFilters((current) => ({ ...current, [field]: value }));
    };

    const clearMemberFilters = () => {
        setMemberFilters({
            search: "",
            lodgeId: "",
            statusId: "",
            memberCategory: "",
            activeOnly: false,
        });
        setMemberQuickFilter(null);
    };

    const applyMemberQuickFilter = (filter) => {
        setMemberQuickFilter(filter);
        if (filter?.category) {
            setMemberFilters((current) => ({ ...current, memberCategory: filter.category, statusId: "" }));
            return;
        }
        if (filter?.statusId) {
            setMemberFilters((current) => ({ ...current, memberCategory: "", statusId: filter.statusId }));
            return;
        }
        if (filter?.type === "nonActiveStatus") {
            setMemberFilters((current) => ({ ...current, memberCategory: "", statusId: "" }));
            return;
        }
        setMemberFilters((current) => ({ ...current, memberCategory: current.memberCategory || "", statusId: current.statusId || "" }));
    };

    const isQuickFilterActive = (type, value = null) => (
        memberQuickFilter?.type === type && (value === null || memberQuickFilter?.value === value)
    );

    const daysUntilRecurringDate = (dateString) => {
        if (!dateString) return null;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const sourceDate = new Date(dateString);
        if (Number.isNaN(sourceDate.getTime())) return null;
        let nextDate = new Date(today.getFullYear(), sourceDate.getMonth(), sourceDate.getDate());
        if (nextDate < today) nextDate = new Date(today.getFullYear() + 1, sourceDate.getMonth(), sourceDate.getDate());
        return Math.round((nextDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    };

    const getServiceYears = (dateString) => {
        if (!dateString) return null;
        const today = new Date();
        const sourceDate = new Date(dateString);
        if (Number.isNaN(sourceDate.getTime())) return null;
        let years = today.getFullYear() - sourceDate.getFullYear();
        const anniversaryThisYear = new Date(today.getFullYear(), sourceDate.getMonth(), sourceDate.getDate());
        if (anniversaryThisYear > today) years -= 1;
        return Math.max(years, 0);
    };

    const getMemberDegreeStage = (member) => {
        if (member.has_master_mason) return "master-mason";
        if (member.has_fellowcraft) return "fellowcraft";
        if (member.has_entered_apprentice || Number(member.degree_count || 0) > 0) return "entered-apprentice";
        return "none";
    };

    const filteredMemberRows = useMemo(() => {
        const rows = memberRows || [];
        if (!memberQuickFilter) return rows;
        const thisYear = new Date().getFullYear();
        return rows.filter((member) => {
            const degreeStage = getMemberDegreeStage(member);
            const latestDegreeDate = member.latest_degree_date ? new Date(member.latest_degree_date) : null;
            const serviceYears = getServiceYears(member.current_membership_start_date);
            switch (memberQuickFilter.type) {
                case "membershipCategory":
                    return (member.current_membership_category || (Number(member.current_membership_count || 0) > 0 ? "current" : "former")) === memberQuickFilter.value;
                case "membershipStatus":
                    return String(member.current_membership_status_id || "") === String(memberQuickFilter.value || "");
                case "membershipStatusLabel":
                    return (member.current_membership_status || "Not recorded") === memberQuickFilter.value;
                case "nonActiveStatus":
                    {
                        const status = String(member.current_membership_status || "").trim().toLowerCase();
                        return status && status !== "active";
                    }
                case "membershipType":
                    return (member.current_membership_type || "Not recorded") === memberQuickFilter.value;
                case "lodge":
                    return String(member.current_lodge_names || member.current_lodge_name || "")
                        .split(",")
                        .map((name) => name.trim())
                        .filter(Boolean)
                        .includes(memberQuickFilter.value);
                case "missingEmail":
                    return !String(member.email_address || "").trim();
                case "missingPhone":
                    return !String(member.phone_number || "").trim();
                case "withoutLodge":
                    return !String(member.current_lodge_names || member.current_lodge_name || "").trim();
                case "degreeStage":
                    return degreeStage === memberQuickFilter.value;
                case "birthdays30":
                    {
                        const daysAway = daysUntilRecurringDate(member.date_of_birth);
                        return daysAway !== null && daysAway <= 30;
                    }
                case "birthdays":
                    return Boolean(member.date_of_birth);
                case "anniversaries":
                    return serviceYears !== null && serviceYears > 0;
                case "serviceAwards":
                    return [25, 40, 50, 60, 70].includes(serviceYears);
                case "degreeProgress":
                    return ["entered-apprentice", "fellowcraft"].includes(degreeStage);
                case "eaOnly":
                    return degreeStage === "entered-apprentice";
                case "fcStage":
                    return degreeStage === "fellowcraft";
                case "raisedThisYear":
                    return degreeStage === "master-mason" && latestDegreeDate && !Number.isNaN(latestDegreeDate.getTime()) && latestDegreeDate.getFullYear() === thisYear;
                default:
                    return true;
            }
        });
    }, [memberRows, memberQuickFilter]);

    const memberSelectOptions = useMemo(() => {
        const memberOptions = (filteredMemberRows || []).map((member) => ({
            value: member.id,
            member,
            photoPath: member.profile_photo_path || "",
            label: [
                `${member.first_name || ""} ${member.last_name || ""}`.trim() || member.id,
                member.current_lodge_names || member.current_lodge_name,
                member.current_membership_status,
            ].filter(Boolean).join(" · "),
        }));
        return [{ value: "new", label: "Add New Member" }, ...memberOptions];
    }, [filteredMemberRows]);

    useEffect(() => {
        if (!memberQuickFilter || !selectedMember) return;
        const stillVisible = (filteredMemberRows || []).some((member) => member.id === selectedMember.id);
        if (stillVisible) return;
        setSelectedMember(null);
        setMemberProfile(null);
        setFormData({});
        setIsAddingNew(false);
    }, [filteredMemberRows, memberQuickFilter, selectedMember]);

    const memberDirectorySummary = useMemo(() => {
        const rows = filteredMemberRows || [];
        const currentMembers = rows.filter((member) => (member.current_membership_category || (Number(member.current_membership_count || 0) > 0 ? "current" : "former")) === "current").length;
        const formerMembers = rows.filter((member) => (member.current_membership_category || (Number(member.current_membership_count || 0) > 0 ? "current" : "former")) === "former").length;
        const activeStatus = rows.filter((member) => String(member.current_membership_status || "").toLowerCase() === "active").length;
        const nonActiveStatus = rows.filter((member) => {
            const status = String(member.current_membership_status || "").trim().toLowerCase();
            return status && status !== "active";
        }).length;
        const withEmail = rows.filter((member) => String(member.email_address || "").trim()).length;
        const withPhone = rows.filter((member) => String(member.phone_number || "").trim()).length;
        const withLodge = rows.filter((member) => String(member.current_lodge_names || member.current_lodge_name || "").trim()).length;
        const lodgeNames = new Set();
        rows.forEach((member) => {
            String(member.current_lodge_names || member.current_lodge_name || "")
                .split(",")
                .map((name) => name.trim())
                .filter(Boolean)
                .forEach((name) => lodgeNames.add(name));
        });
        return {
            shown: rows.length,
            active: currentMembers,
            currentMembers,
            formerMembers,
            activeStatus,
            nonActiveStatus,
            withEmail,
            withPhone,
            withLodge,
            withoutEmail: rows.length - withEmail,
            withoutPhone: rows.length - withPhone,
            withoutLodge: rows.length - withLodge,
            lodgeCount: lodgeNames.size,
        };
    }, [filteredMemberRows]);

    const filteredMemberInsights = useMemo(() => {
        const rows = filteredMemberRows || [];
        const tally = (items) => items.reduce((acc, item) => {
            const label = item || "Not recorded";
            acc.set(label, (acc.get(label) || 0) + 1);
            return acc;
        }, new Map());
        const sortEntries = (map) => [...map.entries()]
            .map(([label, count]) => ({ label, count }))
            .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
        const lodgeMap = new Map();
        rows.forEach((member) => {
            const labels = String(member.current_lodge_names || member.current_lodge_name || "")
                .split(",")
                .map((name) => name.trim())
                .filter(Boolean);
            (labels.length ? labels : ["Not linked"]).forEach((label) => {
                lodgeMap.set(label, (lodgeMap.get(label) || 0) + 1);
            });
        });
        return {
            lodges: sortEntries(lodgeMap),
            statuses: sortEntries(tally(rows.map((member) => member.current_membership_status))).map((item) => ({
                ...item,
                id: rows.find((member) => (member.current_membership_status || "Not recorded") === item.label)?.current_membership_status_id,
            })),
            types: sortEntries(tally(rows.map((member) => member.current_membership_type))),
            contactGaps: [
                { label: "Missing email", count: memberDirectorySummary.withoutEmail },
                { label: "Missing phone", count: memberDirectorySummary.withoutPhone },
                { label: "Not linked to a lodge", count: memberDirectorySummary.withoutLodge },
            ].filter((item) => item.count > 0),
        };
    }, [filteredMemberRows, memberDirectorySummary]);

    const upcomingBirthdays = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const daysUntilBirthday = (dateString) => {
            if (!dateString) return null;
            const sourceDate = new Date(dateString);
            if (Number.isNaN(sourceDate.getTime())) return null;
            let nextBirthday = new Date(today.getFullYear(), sourceDate.getMonth(), sourceDate.getDate());
            if (nextBirthday < today) {
                nextBirthday = new Date(today.getFullYear() + 1, sourceDate.getMonth(), sourceDate.getDate());
            }
            const diffMs = nextBirthday.getTime() - today.getTime();
            return {
                daysAway: Math.round(diffMs / (1000 * 60 * 60 * 24)),
                nextBirthday,
            };
        };
        return (filteredMemberRows || [])
            .map((member) => {
                const birthday = daysUntilBirthday(member.date_of_birth);
                if (!birthday || birthday.daysAway > 90) return null;
                return {
                    id: member.id,
                    name: `${member.first_name || ""} ${member.last_name || ""}`.trim() || "Unnamed member",
                    lodge: member.current_lodge_name || member.current_lodge_names || "Not linked",
                    daysAway: birthday.daysAway,
                    dateLabel: birthday.nextBirthday.toLocaleDateString("en-ZA", { day: "2-digit", month: "short" }),
                };
            })
            .filter(Boolean)
            .sort((a, b) => a.daysAway - b.daysAway || a.name.localeCompare(b.name))
            .slice(0, 8);
    }, [filteredMemberRows]);

    const memberMilestones = useMemo(() => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const daysUntil = (dateString) => {
            if (!dateString) return null;
            const sourceDate = new Date(dateString);
            if (Number.isNaN(sourceDate.getTime())) return null;
            let nextDate = new Date(currentYear, sourceDate.getMonth(), sourceDate.getDate());
            if (nextDate < today) nextDate = new Date(currentYear + 1, sourceDate.getMonth(), sourceDate.getDate());
            return Math.round((nextDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        };
        const yearsSince = (dateString) => {
            if (!dateString) return null;
            const sourceDate = new Date(dateString);
            if (Number.isNaN(sourceDate.getTime())) return null;
            let years = currentYear - sourceDate.getFullYear();
            const anniversaryThisYear = new Date(currentYear, sourceDate.getMonth(), sourceDate.getDate());
            if (anniversaryThisYear > today) years -= 1;
            return Math.max(years, 0);
        };
        const rows = memberRows || [];
        const birthdaysThisYear = rows.filter((member) => Boolean(member.date_of_birth)).length;
        const anniversaries = rows
            .map((member) => ({
                member,
                years: yearsSince(member.current_membership_start_date),
                days: daysUntil(member.current_membership_start_date),
            }))
            .filter((item) => item.years !== null && item.years > 0);
        const serviceAwards = anniversaries.filter((item) => [25, 40, 50, 60, 70].includes(item.years)).length;
        return {
            upcoming30: rows.filter((member) => {
                const daysAway = daysUntilRecurringDate(member.date_of_birth);
                return daysAway !== null && daysAway <= 30;
            }).length,
            birthdaysThisYear,
            anniversaries: anniversaries.length,
            serviceAwards,
        };
    }, [memberRows]);

    const degreeProgressSummary = useMemo(() => {
        const rows = memberRows || [];
        const thisYear = new Date().getFullYear();
        return {
            activeCandidates: rows.filter((member) => ["entered-apprentice", "fellowcraft"].includes(getMemberDegreeStage(member))).length,
            enteredApprentices: rows.filter((member) => getMemberDegreeStage(member) === "entered-apprentice").length,
            fellowcrafts: rows.filter((member) => getMemberDegreeStage(member) === "fellowcraft").length,
            masterMasons: rows.filter((member) => getMemberDegreeStage(member) === "master-mason").length,
            completedThisYear: rows.filter((member) => {
                const degreeDate = member.latest_degree_date ? new Date(member.latest_degree_date) : null;
                return getMemberDegreeStage(member) === "master-mason" && degreeDate && !Number.isNaN(degreeDate.getTime()) && degreeDate.getFullYear() === thisYear;
            }).length,
            noDegreesRecorded: rows.filter((member) => !Number(member.degree_count || 0)).length,
        };
    }, [memberRows]);

    const formatStoredDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return dateString;
        const offsetInMs = 2 * 60 * 60 * 1000;
        return new Date(date.getTime() + offsetInMs).toISOString().split("T")[0];
    };

    const getMemberFullName = (member) => `${member.first_name || ""} ${member.last_name || ""}`.trim() || "Unnamed member";

    const getMemberAddress = (member) => [
        member.address,
        member.city,
        member.state,
        member.postcode,
        countries.find((country) => country.id === member.country_id)?.name,
    ].filter(Boolean).join(", ");

    const getSpouseDisplay = (member) => {
        const spouseName = `${member.spouse_first_name || ""} ${member.spouse_last_name || ""}`.trim();
        const spouseBirth = formatStoredDate(member.spouse_date_of_birth);
        return [spouseName, spouseBirth].filter(Boolean).join(" / ");
    };

    const getMemberCategoryLabel = (member) => {
        const category = member.current_membership_category || (Number(member.current_membership_count || 0) > 0 ? "current" : "former");
        return category === "current" ? "Current member" : "Not a member anymore";
    };

    const getMemberDegreeDates = (member) => {
        const allDegrees = String(member.all_degree_dates || "").trim();
        if (allDegrees) return allDegrees;
        if (member.latest_degree_date) {
            return `${member.latest_degree_name || "Degree"}: ${formatStoredDate(member.latest_degree_date)}`;
        }
        return "";
    };

    const lodgeRollRows = (filteredMemberRows || []).map((member) => ({
        id: member.id,
        name: getMemberFullName(member),
        grade: member.current_membership_type || member.current_membership_status || "",
        status: member.current_membership_status || "",
        address: getMemberAddress(member),
        email: member.email_address || "",
        phone: member.phone_number || "",
        vocation: member.occupation || "",
        birthday: formatStoredDate(member.date_of_birth),
        startDate: formatStoredDate(member.current_membership_start_date),
        endDate: formatStoredDate(member.current_membership_end_date),
        latestDegree: member.latest_degree_name || "",
        latestDegreeDate: formatStoredDate(member.latest_degree_date),
        degreeDates: getMemberDegreeDates(member),
        partner: getSpouseDisplay(member),
    }));

    const renderInsightBars = (items, emptyLabel = "No matching records", onSelect = null, isActive = null) => {
        const max = Math.max(...items.map((item) => item.count), 1);
        if (!items.length) return <p className="member-empty-note">{emptyLabel}</p>;
        return (
            <div className="member-insight-bars">
                {items.slice(0, 8).map((item) => (
                    <button
                        type="button"
                        className={`member-insight-bar-row${isActive?.(item) ? " active" : ""}`}
                        key={item.label}
                        onClick={() => onSelect?.(item)}
                        disabled={!onSelect}
                    >
                        <div>
                            <span>{item.label}</span>
                            <strong>{item.count}</strong>
                        </div>
                        <i style={{ "--bar-width": `${Math.max(6, (item.count / max) * 100)}%` }} />
                    </button>
                ))}
            </div>
        );
    };

    const downloadMemberDirectoryCsv = () => {
        const headers = [
            "Export date",
            "Applied filters",
            "No.",
            "First name",
            "Last name",
            "Full name",
            "Grade / membership type",
            "Membership status",
            "Membership category",
            "Current lodges",
            "Membership start date",
            "Membership end date",
            "Degrees and dates",
            "Date of birth",
            "Date of death",
            "Email",
            "Phone",
            "Occupation",
            "Address",
            "City",
            "State / province",
            "Postcode",
            "Country",
            "Spouse name",
            "Spouse date of birth",
            "Spouse date of death",
            "Spouse occupation",
            "Spouse email",
            "Spouse phone",
            "Comments",
            "Spouse comments",
            "Missing email",
            "Missing phone",
        ];
        const escapeCsv = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
        const exportDate = new Date().toLocaleString("en-ZA", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });
        const filterLabel = getMemberFilterLabel();
        const lines = [
            headers.map(escapeCsv).join(","),
            ...filteredMemberRows.map((member, index) => [
                exportDate,
                filterLabel,
                index + 1,
                member.first_name,
                member.last_name,
                `${member.first_name || ""} ${member.last_name || ""}`.trim(),
                member.current_membership_type,
                member.current_membership_status,
                getMemberCategoryLabel(member),
                member.current_lodge_names || member.current_lodge_name,
                formatStoredDate(member.current_membership_start_date),
                formatStoredDate(member.current_membership_end_date),
                getMemberDegreeDates(member),
                formatStoredDate(member.date_of_birth),
                formatStoredDate(member.date_of_death),
                member.email_address,
                member.phone_number,
                member.occupation,
                member.address,
                member.city,
                member.state,
                member.postcode,
                countries.find((country) => country.id === member.country_id)?.name || "",
                `${member.spouse_first_name || ""} ${member.spouse_last_name || ""}`.trim(),
                formatStoredDate(member.spouse_date_of_birth),
                formatStoredDate(member.spouse_date_of_death),
                member.spouse_occupation,
                member.spouse_email_address,
                member.spouse_phone_number,
                member.comments,
                member.spouse_comments,
                String(member.email_address || "").trim() ? "No" : "Yes",
                String(member.phone_number || "").trim() ? "No" : "Yes",
            ].map(escapeCsv).join(",")),
        ];
        const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `member-directory-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    };

    const getMemberFilterLabel = () => [
        memberFilters.search.trim() ? `Search: ${memberFilters.search.trim()}` : null,
        memberFilters.lodgeId ? `Lodge: ${lodges.find((lodge) => lodge.id === memberFilters.lodgeId)?.name || memberFilters.lodgeId}` : null,
        memberFilters.memberCategory === "current" ? "Category: Current members" : null,
        memberFilters.memberCategory === "former" ? "Category: Not a member anymore" : null,
        memberFilters.statusId ? `Status: ${membershipStatuses.find((status) => status.id === memberFilters.statusId)?.name || memberFilters.statusId}` : null,
        memberFilters.activeOnly ? "Active only: Yes" : null,
    ].filter(Boolean).join("; ") || "None";

    const drawPdfBarList = (doc, title, items, x, y, width, color) => {
        doc.setFontSize(12);
        doc.setFont(undefined, "bold");
        doc.text(title, x, y);
        const max = Math.max(...items.map((item) => item.count), 1);
        let currentY = y + 7;
        items.slice(0, 7).forEach((item) => {
            const barWidth = Math.max(4, (item.count / max) * width);
            doc.setFontSize(8.5);
            doc.setFont(undefined, "normal");
            doc.setTextColor(51, 65, 85);
            doc.text(String(item.label).slice(0, 45), x, currentY);
            doc.setTextColor(15, 23, 42);
            doc.text(String(item.count), x + width + 6, currentY, { align: "right" });
            doc.setFillColor(226, 232, 240);
            doc.roundedRect(x, currentY + 2, width, 3.5, 1.5, 1.5, "F");
            doc.setFillColor(...color);
            doc.roundedRect(x, currentY + 2, barWidth, 3.5, 1.5, 1.5, "F");
            currentY += 11;
        });
        doc.setTextColor(15, 23, 42);
        return currentY;
    };

    const downloadMemberDirectoryPdf = () => {
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 12;
        const filterLabel = getMemberFilterLabel();
        const generatedLabel = new Date().toLocaleString("en-ZA", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
        });

        doc.setFillColor(15, 23, 42);
        doc.rect(0, 0, pageWidth, 34, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont(undefined, "bold");
        doc.text("Member Directory Summary", margin, 15);
        doc.setFontSize(9);
        doc.setFont(undefined, "normal");
        doc.text(`Generated ${generatedLabel}`, margin, 23);
        doc.text(`Filters: ${filterLabel}`, margin, 29);

        const summaryCards = [
            ["Shown", memberDirectorySummary.shown],
            ["Current members", memberDirectorySummary.active],
            ["Lodges represented", memberDirectorySummary.lodgeCount],
            ["Degree progress", degreeProgressSummary.activeCandidates],
            ["Missing email", memberDirectorySummary.withoutEmail],
            ["Missing phone", memberDirectorySummary.withoutPhone],
        ];
        const cardWidth = (pageWidth - margin * 2 - 20) / 6;
        let x = margin;
        summaryCards.forEach(([label, value]) => {
            doc.setFillColor(248, 250, 252);
            doc.setDrawColor(216, 227, 241);
            doc.roundedRect(x, 42, cardWidth, 20, 2, 2, "FD");
            doc.setTextColor(15, 23, 42);
            doc.setFontSize(15);
            doc.setFont(undefined, "bold");
            doc.text(String(value), x + 4, 51);
            doc.setTextColor(100, 116, 139);
            doc.setFontSize(7.5);
            doc.text(label.toUpperCase(), x + 4, 58);
            x += cardWidth + 4;
        });

        drawPdfBarList(doc, "Members by lodge", filteredMemberInsights.lodges, margin, 77, 84, [20, 184, 166]);
        drawPdfBarList(doc, "Status mix", filteredMemberInsights.statuses, 112, 77, 64, [37, 99, 235]);
        drawPdfBarList(doc, "Membership types", filteredMemberInsights.types, 192, 77, 64, [14, 116, 144]);

        doc.autoTable({
            startY: 158,
            margin: { left: margin, right: margin },
            head: [["Name", "Email", "Phone", "Current lodges", "Status", "Type", "Key dates"]],
            body: filteredMemberRows.map((member) => [
                `${member.first_name || ""} ${member.last_name || ""}`.trim(),
                member.email_address || "",
                member.phone_number || "",
                member.current_lodge_names || member.current_lodge_name || "",
                member.current_membership_status || "",
                member.current_membership_type || "",
                [
                    member.date_of_birth ? `Birth ${formatStoredDate(member.date_of_birth)}` : null,
                    member.current_membership_start_date ? `Joined ${formatStoredDate(member.current_membership_start_date)}` : null,
                    member.current_membership_end_date ? `Ended ${formatStoredDate(member.current_membership_end_date)}` : null,
                    getMemberDegreeDates(member) || null,
                ].filter(Boolean).join("\n"),
            ]),
            styles: { fontSize: 7.5, cellPadding: 2 },
            headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255] },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: {
                0: { cellWidth: 36 },
                1: { cellWidth: 42 },
                2: { cellWidth: 28 },
                3: { cellWidth: 70 },
                4: { cellWidth: 24 },
                5: { cellWidth: 32 },
                6: { cellWidth: 42 },
            },
        });

        const finalY = doc.lastAutoTable.finalY + 10;
        if (finalY < 185 && upcomingBirthdays.length) {
            doc.setFontSize(12);
            doc.setFont(undefined, "bold");
            doc.setTextColor(15, 23, 42);
            doc.text("Upcoming birthdays", margin, finalY);
            doc.autoTable({
                startY: finalY + 4,
                margin: { left: margin, right: pageWidth - 140 },
                head: [["Member", "Date", "In", "Lodge"]],
                body: upcomingBirthdays.map((birthday) => [
                    birthday.name,
                    birthday.dateLabel,
                    birthday.daysAway === 0 ? "Today" : `${birthday.daysAway} days`,
                    birthday.lodge,
                ]),
                styles: { fontSize: 7.5, cellPadding: 2 },
                headStyles: { fillColor: [20, 184, 166], textColor: [255, 255, 255] },
            });
        }

        const pageCount = doc.internal.getNumberOfPages();
        for (let page = 1; page <= pageCount; page += 1) {
            doc.setPage(page);
            doc.setFontSize(7);
            doc.setTextColor(100, 116, 139);
            doc.text(`Page ${page} of ${pageCount}`, pageWidth - margin, 202, { align: "right" });
        }
        doc.save(`member-summary-${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    const downloadLodgeRollPdf = () => {
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 10;
        const lodgeLabel = memberFilters.lodgeId
            ? lodges.find((lodge) => lodge.id === memberFilters.lodgeId)?.name || "Selected Lodge"
            : "Filtered Member Directory";
        const generatedLabel = new Date().toLocaleDateString("en-ZA", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });

        doc.setFontSize(15);
        doc.setFont(undefined, "bold");
        doc.text(lodgeLabel.toUpperCase(), pageWidth / 2, 12, { align: "center" });
        doc.setFontSize(9);
        doc.setFont(undefined, "normal");
        doc.text(`Names, addresses and telephone numbers as at ${generatedLabel}`, pageWidth / 2, 18, { align: "center" });
        doc.text(`Filters: ${getMemberFilterLabel()}`, pageWidth / 2, 24, { align: "center" });

        doc.autoTable({
            startY: 30,
            margin: { left: margin, right: margin },
            head: [["No.", "Name", "Grade", "Status", "Dates", "Address", "E-mail", "Phone / Mobile", "Vocation", "Partner / Birthday"]],
            body: lodgeRollRows.map((member, index) => [
                index + 1,
                member.name,
                member.grade,
                member.status,
                [
                    member.birthday ? `Birth: ${member.birthday}` : null,
                    member.startDate ? `Start: ${member.startDate}` : null,
                    member.endDate ? `End: ${member.endDate}` : null,
                    member.degreeDates || null,
                ].filter(Boolean).join("\n"),
                member.address,
                member.email,
                member.phone,
                member.vocation,
                member.partner,
            ]),
            styles: {
                fontSize: 7,
                cellPadding: 1.6,
                overflow: "linebreak",
                lineColor: [148, 163, 184],
                lineWidth: 0.12,
            },
            headStyles: {
                fillColor: [203, 213, 225],
                textColor: [15, 23, 42],
                fontStyle: "bold",
                halign: "center",
            },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            columnStyles: {
                0: { cellWidth: 10, halign: "center" },
                1: { cellWidth: 36 },
                2: { cellWidth: 24 },
                3: { cellWidth: 22 },
                4: { cellWidth: 30 },
                5: { cellWidth: 44 },
                6: { cellWidth: 38 },
                7: { cellWidth: 26 },
                8: { cellWidth: 30 },
                9: { cellWidth: 28 },
            },
        });

        const pageCount = doc.internal.getNumberOfPages();
        for (let page = 1; page <= pageCount; page += 1) {
            doc.setPage(page);
            doc.setFontSize(7);
            doc.setTextColor(100, 116, 139);
            doc.text(`Page ${page} of ${pageCount}`, pageWidth - margin, 202, { align: "right" });
        }
        doc.save(`lodge-member-list-${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    const fetchMemberProfile = async (memberId) => {
        if (!memberId) {
            setMemberProfile(null);
            return;
        }

        try {
            const res = await axios.get(`/api/resources/members/${memberId}/profile`);
            setMemberProfile(res.data);
        } catch (error) {
            console.error("Error fetching member profile:", error);
            setMemberProfile(null);
        }
    };

    const handleMemberSelect = async (selectedOption) => {
        if (!selectedOption || selectedOption.value === "new") {
            setSelectedMember(null);
            setMemberProfile(null);
            setFormData({});
            setIsAddingNew(true);
            setActiveTab("overview");
            return;
        }

        try {
            const res = await axios.get(`/api/resources/members/${selectedOption.value}`);
            const member = res.data;

            setSelectedMember(member);
            setFormData({
                ...member,
                date_of_birth: member.date_of_birth ? formatToDateInput(member.date_of_birth) : "",
                date_of_death: member.date_of_death ? formatToDateInput(member.date_of_death) : "",
                spouse_date_of_birth: member.spouse_date_of_birth ? formatToDateInput(member.spouse_date_of_birth) : "",
                spouse_date_of_death: member.spouse_date_of_death ? formatToDateInput(member.spouse_date_of_death) : "",
            });
            setIsAddingNew(false);
            setActiveTab("overview");
            await fetchMemberProfile(member.id);
        } catch (error) {
            console.error("Error fetching member details:", error);
        }
    };

    const formatToDateInput = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const offsetInMs = 2 * 60 * 60 * 1000;
        const localDate = new Date(date.getTime() + offsetInMs);
        return localDate.toISOString().split("T")[0];
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Unknown";
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return dateString;
        return formatToDateInput(dateString);
    };

    const fullName = useMemo(() => {
        const first = formData.first_name || selectedMember?.first_name || "";
        const last = formData.last_name || selectedMember?.last_name || "";
        return `${first} ${last}`.trim() || "New Member";
    }, [formData.first_name, formData.last_name, selectedMember]);

    const titledFullName = useMemo(() => {
        const title = String(formData.current_title || selectedMember?.current_title || "").trim();
        return [title, fullName].filter(Boolean).join(" ");
    }, [formData.current_title, fullName, selectedMember]);

    const getInitials = (nameOrMember) => {
        const name = typeof nameOrMember === "string"
            ? nameOrMember
            : `${nameOrMember?.first_name || ""} ${nameOrMember?.last_name || ""}`;
        return name
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0])
            .join("")
            .toUpperCase() || "M";
    };

    const getPhotoUrl = (photoPath) => {
        const cleanPath = String(photoPath || "").trim();
        if (!cleanPath) return "";
        return cleanPath.startsWith("http") ? cleanPath : cleanPath;
    };

    const currentPhotoUrl = getPhotoUrl(formData.profile_photo_path || selectedMember?.profile_photo_path);

    const renderMemberAvatar = (nameOrMember, photoPath, className = "member-avatar") => {
        const photoUrl = getPhotoUrl(photoPath);
        if (photoUrl) {
            return (
                <div className={`${className} has-photo`}>
                    <img src={photoUrl} alt="" />
                </div>
            );
        }
        return <div className={className}>{getInitials(nameOrMember)}</div>;
    };

    const formatMemberOptionLabel = (option) => {
        if (option.value === "new") {
            return (
                <div className="member-select-option">
                    <div className="member-select-avatar add">+</div>
                    <div>
                        <strong>Add New Member</strong>
                        <span>Create a new record</span>
                    </div>
                </div>
            );
        }

        const member = option.member || {};
        const name = `${member.first_name || ""} ${member.last_name || ""}`.trim() || option.label;
        const detail = [
            member.current_lodge_names || member.current_lodge_name,
            member.current_membership_type,
            member.current_membership_status,
        ].filter(Boolean).join(" · ") || "No lodge details";

        return (
            <div className="member-select-option">
                {renderMemberAvatar(member, option.photoPath, "member-select-avatar")}
                <div>
                    <strong>{name}</strong>
                    <span>{detail}</span>
                </div>
            </div>
        );
    };

    const currentCountry = useMemo(() => {
        const countryId = formData.country_id || selectedMember?.country_id;
        return countries.find((country) => country.id === countryId)?.name || memberProfile?.country_name || "Not recorded";
    }, [countries, formData.country_id, memberProfile, selectedMember]);

    const currentMembership = useMemo(() => {
        const history = memberProfile?.membership_history || [];
        return [...history].sort((a, b) => {
            const aCurrent = a.membership_category === "current" || (!a.membership_category && !a.end_date);
            const bCurrent = b.membership_category === "current" || (!b.membership_category && !b.end_date);
            if (aCurrent !== bCurrent) return Number(bCurrent) - Number(aCurrent);
            if (Boolean(a.is_mother_lodge) !== Boolean(b.is_mother_lodge)) {
                return Boolean(b.is_mother_lodge) - Boolean(a.is_mother_lodge);
            }
            const aEnd = a.end_date ? new Date(a.end_date).getTime() : Number.MAX_SAFE_INTEGER;
            const bEnd = b.end_date ? new Date(b.end_date).getTime() : Number.MAX_SAFE_INTEGER;
            return bEnd - aEnd;
        })[0];
    }, [memberProfile]);

    const profileCounts = {
        history: memberProfile?.membership_history?.length || 0,
        degrees: memberProfile?.degrees?.length || 0,
        roles: memberProfile?.roles?.length || 0,
        meetings: memberProfile?.meetings?.length || 0,
    };

    const refreshSelectedMember = async () => {
        if (!selectedMember?.id) return;
        const res = await axios.get(`/api/resources/members/${selectedMember.id}`);
        const member = res.data;
        setSelectedMember(member);
        setFormData({
            ...member,
            date_of_birth: member.date_of_birth ? formatToDateInput(member.date_of_birth) : "",
            date_of_death: member.date_of_death ? formatToDateInput(member.date_of_death) : "",
            spouse_date_of_birth: member.spouse_date_of_birth ? formatToDateInput(member.spouse_date_of_birth) : "",
            spouse_date_of_death: member.spouse_date_of_death ? formatToDateInput(member.spouse_date_of_death) : "",
        });
        await fetchMemberProfile(member.id);
    };

    const handleSave = async () => {
        if (!validateForm()) {
            alert("Please fill in all required fields.");
            return;
        }

        const confirmSave = window.confirm(
            selectedMember ? "Are you sure you want to update this member?" : "Are you sure you want to add this member?"
        );
        if (!confirmSave) return;

        try {
            const payload = {
                ...formData,
                date_of_birth: formData.date_of_birth || null,
                date_of_death: formData.date_of_death || null,
                country_id: formData.country_id || null,
                spouse_date_of_birth: formData.spouse_date_of_birth || null,
                spouse_date_of_death: formData.spouse_date_of_death || null,
            };

            const res = selectedMember
                ? await axios.put(`/api/resources/members/${selectedMember.id}`, payload)
                : await axios.post("/api/resources/members", payload);

            await fetchMembers();
            await fetchMemberCount();
            setSelectedMember(res.data);
            setIsAddingNew(false);
            await fetchMemberProfile(res.data.id);
            alert("Member saved successfully.");
            if (typeof onSave === "function") onSave();
        } catch (error) {
            console.error("Error saving member:", error);
            alert("Error saving member. Please try again.");
        }
    };

    const handlePhotoSelected = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!selectedMember?.id) {
            alert("Save the member first, then add a photo.");
            event.target.value = "";
            return;
        }

        if (!file.type.startsWith("image/")) {
            alert("Please choose an image file.");
            event.target.value = "";
            return;
        }

        const data = new FormData();
        data.append("photo", file);

        try {
            setPhotoUploading(true);
            const res = await axios.post(`/api/resources/members/${selectedMember.id}/photo`, data, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const nextPath = res.data?.profile_photo_path || res.data?.member?.profile_photo_path || "";
            setFormData((prev) => ({ ...prev, profile_photo_path: nextPath }));
            setSelectedMember((prev) => prev ? { ...prev, profile_photo_path: nextPath } : prev);
            await fetchMembers();
            if (selectedMember?.id) await fetchMemberProfile(selectedMember.id);
        } catch (error) {
            console.error("Error uploading member photo:", error);
            alert(error.response?.data?.error || "Could not upload this member photo.");
        } finally {
            setPhotoUploading(false);
            event.target.value = "";
        }
    };

    const handleRemovePhoto = async () => {
        if (!selectedMember?.id) return;
        if (!window.confirm("Remove this member photo?")) return;

        try {
            setPhotoUploading(true);
            await axios.delete(`/api/resources/members/${selectedMember.id}/photo`);
            setFormData((prev) => ({ ...prev, profile_photo_path: "" }));
            setSelectedMember((prev) => prev ? { ...prev, profile_photo_path: "" } : prev);
            await fetchMembers();
        } catch (error) {
            console.error("Error removing member photo:", error);
            alert(error.response?.data?.error || "Could not remove this member photo.");
        } finally {
            setPhotoUploading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        if (errors[name]) {
            setErrors((prevErrors) => ({ ...prevErrors, [name]: null }));
        }
    };

    const validateForm = () => {
        const requiredFields = ["first_name", "last_name"];
        const newErrors = {};

        requiredFields.forEach((field) => {
            if (!formData[field] || formData[field].toString().trim() === "") {
                newErrors[field] = `${field.replace(/_/g, " ")} is required.`;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleDelete = async () => {
        if (!selectedMember) return;

        const confirmDelete = window.confirm("Are you sure you want to delete this member?");
        if (!confirmDelete) return;

        try {
            await axios.delete(`/api/resources/members/${selectedMember.id}`);
            await fetchMembers();
            await fetchMemberCount();
            setFormData({});
            setSelectedMember(null);
            setMemberProfile(null);
            setIsAddingNew(false);
            alert("Member deleted successfully.");
        } catch (error) {
            console.error("Error deleting member:", error);
            alert("Error deleting member. Please try again.");
        }
    };

    const closeChildModal = async () => {
        setCurrentModal(null);
        await refreshSelectedMember();
    };

    const beginMembershipEdit = (item) => {
        setEditingMembershipId(item.id);
        setMembershipEditData({
            lodge_id: item.lodge_id || "",
            membership_type_id: item.membership_type_id || "",
            membership_status_id: item.membership_status_id || "",
            membership_category: item.membership_category || "",
            start_date: formatToDateInput(item.start_date),
            end_date: item.end_date ? formatToDateInput(item.end_date) : "",
            comments: item.comments || "",
            proposer_id: item.proposer_id || null,
            seconder_id: item.seconder_id || null,
            is_mother_lodge: Boolean(item.is_mother_lodge),
        });
    };

    const updateMembershipEdit = (field, value) => {
        setMembershipEditData((current) => ({ ...current, [field]: value }));
    };

    const getInlineMembershipCategory = () => {
        if (membershipEditData.membership_category) return membershipEditData.membership_category;

        const selectedStatus = membershipStatuses.find((status) => status.id === membershipEditData.membership_status_id);
        const statusName = String(selectedStatus?.name || "").toLowerCase();
        if (statusName === "active" && !membershipEditData.end_date) return "current";
        if (statusName === "non-active" || membershipEditData.end_date) return "former";
        return "";
    };

    const updateInlineMembershipCategory = (option) => {
        setMembershipEditData((current) => ({
            ...current,
            membership_category: option?.value || "",
        }));
    };

    const cancelMembershipEdit = () => {
        setEditingMembershipId(null);
        setMembershipEditData({});
    };

    const saveMembershipEdit = async () => {
        if (!selectedMember || !editingMembershipId) return;
        if (!membershipEditData.lodge_id) {
            alert("Lodge is required.");
            return;
        }
        try {
            await axios.put(`/api/resources/members/${selectedMember.id}/history/${editingMembershipId}`, {
                ...membershipEditData,
                membership_type_id: membershipEditData.membership_type_id || null,
                membership_status_id: membershipEditData.membership_status_id || null,
                membership_category: membershipEditData.membership_category || null,
                start_date: membershipEditData.start_date || null,
                end_date: membershipEditData.end_date || null,
                proposer_id: membershipEditData.proposer_id || null,
                seconder_id: membershipEditData.seconder_id || null,
                is_mother_lodge: Boolean(membershipEditData.is_mother_lodge),
            });
            cancelMembershipEdit();
            await refreshSelectedMember();
            await fetchMembers();
        } catch (error) {
            console.error("Error updating membership history:", error);
            alert("Could not update this membership record.");
        }
    };

    const tabs = [
        { key: "overview", label: "Overview" },
        { key: "membership", label: "Membership" },
        { key: "degrees", label: "Degrees" },
        { key: "officers", label: "Offices" },
        { key: "meetings", label: "Meetings" },
        { key: "contact", label: "Contact" },
        { key: "spouse", label: "Spouse" },
    ];

    return (
        <div className="member-modal-overlay">
            <div className="member-modal-content">
                <button className="member-close-btn" onClick={onClose}>&times;</button>

                <div className="member-workspace-header">
                    <div>
                        <p className="member-eyebrow">Member Directory</p>
                        <h2>{isAddingNew ? "Add New Member" : "Member Workspace"}</h2>
                    </div>
                    <span>{memberCount} total</span>
                </div>

                <div className="member-filter-panel">
                    <label>
                        Search
                        <input
                            type="search"
                            value={memberFilters.search}
                            onChange={(event) => updateMemberFilter("search", event.target.value)}
                            placeholder="Name, email, or phone"
                        />
                    </label>
                    <label>
                        Lodge
                        <Select
                            value={lodges
                                .map((lodge) => ({
                                    value: lodge.id,
                                    label: [lodge.name, lodge.code ? `No. ${lodge.code}` : null].filter(Boolean).join(" "),
                                }))
                                .find((option) => option.value === memberFilters.lodgeId) || null}
                            onChange={(option) => updateMemberFilter("lodgeId", option?.value || "")}
                            options={lodges.map((lodge) => ({
                                value: lodge.id,
                                label: [lodge.name, lodge.code ? `No. ${lodge.code}` : null].filter(Boolean).join(" "),
                            }))}
                            placeholder="All lodges"
                            isClearable
                            isSearchable
                        />
                    </label>
                    <label>
                        Category
                        <Select
                            value={[
                                { value: "current", label: "Current members" },
                                { value: "former", label: "Not a member anymore" },
                                { value: "", label: "All members" },
                            ].find((option) => option.value === memberFilters.memberCategory)}
                            onChange={(option) => updateMemberFilter("memberCategory", option?.value || "")}
                            options={[
                                { value: "current", label: "Current members" },
                                { value: "former", label: "Not a member anymore" },
                                { value: "", label: "All members" },
                            ]}
                            isSearchable={false}
                        />
                    </label>
                    <label>
                        Status
                        <Select
                            value={membershipStatuses
                                .map((status) => ({ value: status.id, label: status.name }))
                                .find((option) => option.value === memberFilters.statusId) || null}
                            onChange={(option) => updateMemberFilter("statusId", option?.value || "")}
                            options={membershipStatuses.map((status) => ({ value: status.id, label: status.name }))}
                            placeholder="All statuses"
                            isClearable
                            isSearchable
                        />
                    </label>
                    <label className="member-filter-toggle">
                        <input
                            type="checkbox"
                            checked={memberFilters.activeOnly}
                            onChange={(event) => updateMemberFilter("activeOnly", event.target.checked)}
                        />
                        Active only
                    </label>
                    <button type="button" onClick={clearMemberFilters}>Clear</button>
                </div>

                <div className="member-form-group member-select-row">
                    <label>Select Member <span>{Math.max(memberSelectOptions.length - 1, 0)} shown</span></label>
                    <Select
                        options={memberSelectOptions}
                        onChange={handleMemberSelect}
                        value={
                            selectedMember
                                ? memberSelectOptions.find((option) => option.value === selectedMember.id) || {
                                    value: selectedMember.id,
                                    label: `${selectedMember.first_name} ${selectedMember.last_name}`,
                                    member: selectedMember,
                                    photoPath: selectedMember.profile_photo_path || "",
                                }
                                : isAddingNew
                                    ? { value: "new", label: "Add New Member" }
                                    : null
                        }
                        formatOptionLabel={formatMemberOptionLabel}
                        classNamePrefix="member-select"
                        isSearchable
                        placeholder="Search members or add a new member"
                    />
                </div>

                <div className="member-directory-summary">
                    <button
                        type="button"
                        className={`member-summary-tile${isQuickFilterActive("membershipCategory", "current") ? " active" : ""}`}
                        onClick={() => applyMemberQuickFilter({ type: "membershipCategory", value: "current", category: "current", label: "Current members" })}
                    >
                        <strong>{memberDirectorySummary.currentMembers}</strong><span>Current members</span>
                    </button>
                    <button
                        type="button"
                        className={`member-summary-tile${isQuickFilterActive("membershipCategory", "former") ? " active" : ""}`}
                        onClick={() => applyMemberQuickFilter({ type: "membershipCategory", value: "former", category: "former", label: "Not a member anymore" })}
                    >
                        <strong>{memberDirectorySummary.formerMembers}</strong><span>Not current</span>
                    </button>
                    <button
                        type="button"
                        className={`member-summary-tile${memberFilters.statusId && String(membershipStatuses.find((status) => status.id === memberFilters.statusId)?.name || "").toLowerCase() === "active" ? " active" : ""}`}
                        onClick={() => {
                            const activeStatus = membershipStatuses.find((status) => String(status.name || "").toLowerCase() === "active");
                            if (activeStatus) applyMemberQuickFilter({ type: "membershipStatus", value: activeStatus.id, statusId: activeStatus.id, label: "Status: Active" });
                        }}
                    >
                        <strong>{memberDirectorySummary.activeStatus}</strong><span>Active status</span>
                    </button>
                    <button
                        type="button"
                        className={`member-summary-tile${isQuickFilterActive("nonActiveStatus") ? " active" : ""}`}
                        onClick={() => applyMemberQuickFilter({ type: "nonActiveStatus", label: "Statuses other than Active" })}
                    >
                        <strong>{memberDirectorySummary.nonActiveStatus}</strong><span>Non-active status</span>
                    </button>
                    <button type="button" onClick={downloadMemberDirectoryCsv} disabled={!memberRows.length}>
                        Download CSV
                    </button>
                    <button type="button" onClick={downloadMemberDirectoryPdf} disabled={!memberRows.length}>
                        Summary PDF
                    </button>
                    <button type="button" onClick={downloadLodgeRollPdf} disabled={!memberRows.length}>
                        Lodge PDF
                    </button>
                </div>

                {memberQuickFilter && (
                    <div className="member-quick-filter-banner">
                        <span>Filtered by: {memberQuickFilter.label}</span>
                        <button type="button" onClick={() => setMemberQuickFilter(null)}>Show all matching members</button>
                    </div>
                )}

                <div className="member-secretary-dashboard">
                    <section className="member-secretary-brief">
                        <div>
                            <p className="member-eyebrow">Secretary View</p>
                            <h3>Membership Summary</h3>
                        </div>
                        <p>
                            {memberDirectorySummary.shown} record{memberDirectorySummary.shown === 1 ? "" : "s"} match the current filters.
                            {" "}{memberDirectorySummary.currentMembers} are current members, {memberDirectorySummary.activeStatus} are Active,
                            and {memberDirectorySummary.nonActiveStatus} have another membership status.
                        </p>
                        <div className="member-secretary-actions">
                            <button type="button" onClick={() => setShowLodgeRollView(true)} disabled={!memberRows.length}>View Lodge List</button>
                            <button type="button" onClick={downloadLodgeRollPdf} disabled={!memberRows.length}>Quick PDF</button>
                        </div>
                    </section>
                    <section className="member-insight-card member-insight-card-wide">
                        <h4>Current Members By Lodge</h4>
                        {renderInsightBars(
                            filteredMemberInsights.lodges,
                            "No linked lodges in this filtered set",
                            (item) => applyMemberQuickFilter({ type: "lodge", value: item.label, label: `Lodge: ${item.label}` }),
                            (item) => isQuickFilterActive("lodge", item.label)
                        )}
                    </section>
                    <section className="member-insight-card member-milestone-card">
                        <h4>Membership Category</h4>
                        <div className="member-mini-stats">
                            <button
                                type="button"
                                className={isQuickFilterActive("membershipCategory", "current") ? "active" : ""}
                                onClick={() => applyMemberQuickFilter({ type: "membershipCategory", value: "current", category: "current", label: "Current members" })}
                            >
                                <strong>{memberDirectorySummary.currentMembers}</strong><span>Current members</span>
                            </button>
                            <button
                                type="button"
                                className={isQuickFilterActive("membershipCategory", "former") ? "active" : ""}
                                onClick={() => applyMemberQuickFilter({ type: "membershipCategory", value: "former", category: "former", label: "Not a member anymore" })}
                            >
                                <strong>{memberDirectorySummary.formerMembers}</strong><span>Not current</span>
                            </button>
                            <button type="button" onClick={() => applyMemberQuickFilter({ type: "birthdays30", label: "Birthdays in the next 30 days" })}><strong>{memberMilestones.upcoming30}</strong><span>Next birthday 30 days</span></button>
                            <button type="button" onClick={() => applyMemberQuickFilter({ type: "birthdays", label: "Members with birthdays on file" })}><strong>{memberMilestones.birthdaysThisYear}</strong><span>Birthdays on file</span></button>
                        </div>
                    </section>
                    <section className="member-insight-card member-degree-card">
                        <h4>Current Degree Stage</h4>
                        <div className="member-mini-stats">
                            <button type="button" className={isQuickFilterActive("degreeProgress") ? "active" : ""} onClick={() => applyMemberQuickFilter({ type: "degreeProgress", label: "Members with degree progress still open" })}><strong>{degreeProgressSummary.activeCandidates}</strong><span>In progress</span></button>
                            <button type="button" className={isQuickFilterActive("degreeStage", "entered-apprentice") ? "active" : ""} onClick={() => applyMemberQuickFilter({ type: "degreeStage", value: "entered-apprentice", label: "1st degree / Entered Apprentice" })}><strong>{degreeProgressSummary.enteredApprentices}</strong><span>1st / EA</span></button>
                            <button type="button" className={isQuickFilterActive("degreeStage", "fellowcraft") ? "active" : ""} onClick={() => applyMemberQuickFilter({ type: "degreeStage", value: "fellowcraft", label: "2nd degree / Fellowcraft" })}><strong>{degreeProgressSummary.fellowcrafts}</strong><span>2nd / FC</span></button>
                            <button type="button" className={isQuickFilterActive("degreeStage", "master-mason") ? "active" : ""} onClick={() => applyMemberQuickFilter({ type: "degreeStage", value: "master-mason", label: "3rd degree / Master Mason" })}><strong>{degreeProgressSummary.masterMasons}</strong><span>3rd / MM</span></button>
                        </div>
                    </section>
                    <section className="member-insight-card">
                        <h4>Active Status</h4>
                        <div className="member-mini-stats">
                            <button
                                type="button"
                                className={memberFilters.statusId && String(membershipStatuses.find((status) => status.id === memberFilters.statusId)?.name || "").toLowerCase() === "active" ? "active" : ""}
                                onClick={() => {
                                    const activeStatus = membershipStatuses.find((status) => String(status.name || "").toLowerCase() === "active");
                                    if (activeStatus) applyMemberQuickFilter({ type: "membershipStatus", value: activeStatus.id, statusId: activeStatus.id, label: "Status: Active" });
                                }}
                            >
                                <strong>{memberDirectorySummary.activeStatus}</strong><span>Active</span>
                            </button>
                            <button type="button" className={isQuickFilterActive("nonActiveStatus") ? "active" : ""} onClick={() => applyMemberQuickFilter({ type: "nonActiveStatus", label: "Statuses other than Active" })}><strong>{memberDirectorySummary.nonActiveStatus}</strong><span>Non-active</span></button>
                        </div>
                        {renderInsightBars(
                            filteredMemberInsights.statuses,
                            "No statuses in this filtered set",
                            (item) => item.id
                                ? applyMemberQuickFilter({ type: "membershipStatus", value: item.id, statusId: item.id, label: `Status: ${item.label}` })
                                : applyMemberQuickFilter({ type: "membershipStatusLabel", value: item.label, label: `Status: ${item.label}` }),
                            (item) => item.id ? isQuickFilterActive("membershipStatus", item.id) : isQuickFilterActive("membershipStatusLabel", item.label)
                        )}
                    </section>
                    <section className="member-insight-card">
                        <h4>Membership Types</h4>
                        {renderInsightBars(
                            filteredMemberInsights.types,
                            "No membership types in this filtered set",
                            (item) => applyMemberQuickFilter({ type: "membershipType", value: item.label, label: `Membership type: ${item.label}` }),
                            (item) => isQuickFilterActive("membershipType", item.label)
                        )}
                    </section>
                    <section className="member-insight-card">
                        <h4>Contact Gaps</h4>
                        {renderInsightBars(
                            filteredMemberInsights.contactGaps,
                            "No contact gaps in this filtered set",
                            (item) => {
                                const filterType = item.label === "Missing email" ? "missingEmail" : item.label === "Missing phone" ? "missingPhone" : "withoutLodge";
                                applyMemberQuickFilter({ type: filterType, label: item.label });
                            },
                            (item) => isQuickFilterActive(item.label === "Missing email" ? "missingEmail" : item.label === "Missing phone" ? "missingPhone" : "withoutLodge")
                        )}
                    </section>
                    <section className="member-insight-card">
                        <h4>Upcoming Birthdays</h4>
                        {upcomingBirthdays.length ? (
                            <div className="member-birthday-list">
                                {upcomingBirthdays.map((birthday) => (
                                    <div key={birthday.id}>
                                        <span>{birthday.dateLabel}</span>
                                        <strong>{birthday.name}</strong>
                                        <em>{birthday.daysAway === 0 ? "Today" : `${birthday.daysAway} days`} · {birthday.lodge}</em>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="member-empty-note">No birthdays in the next 90 days</p>
                        )}
                    </section>
                </div>

                {(selectedMember || isAddingNew) && (
                    <div className="member-workspace">
                        <aside className="member-profile-panel">
                            {renderMemberAvatar(fullName, currentPhotoUrl)}
                            <h3>{titledFullName}</h3>
                            <p>{formData.occupation || "Occupation not recorded"}</p>
                            <div className="member-chip-row">
                                <span>{currentMembership?.membership_status || "No status"}</span>
                                <span>{currentMembership?.membership_type || "No type"}</span>
                            </div>

                            <dl className="member-profile-list">
                                <div><dt>Current Lodge</dt><dd>{currentMembership?.lodge_name || "Not linked"}</dd></div>
                                <div><dt>Birth</dt><dd>{formData.date_of_birth || "Not recorded"}</dd></div>
                                <div><dt>Death</dt><dd>{formData.date_of_death || "Not recorded"}</dd></div>
                                <div><dt>Country</dt><dd>{currentCountry}</dd></div>
                                <div><dt>Email</dt><dd>{formData.email_address || "Not recorded"}</dd></div>
                                <div><dt>Phone</dt><dd>{formData.phone_number || "Not recorded"}</dd></div>
                            </dl>

                            <div className="member-kpi-grid">
                                <div><strong>{profileCounts.history}</strong><span>History</span></div>
                                <div><strong>{profileCounts.degrees}</strong><span>Degrees</span></div>
                                <div><strong>{profileCounts.roles}</strong><span>Offices</span></div>
                                <div><strong>{profileCounts.meetings}</strong><span>Meetings</span></div>
                            </div>
                        </aside>

                        <section className="member-editor-panel">
                            <div className="member-tabs">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.key}
                                        type="button"
                                        className={activeTab === tab.key ? "active" : ""}
                                        onClick={() => setActiveTab(tab.key)}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            {activeTab === "overview" && (
                                <div className="member-tab-panel">
                                    <div className="member-panel-heading">
                                        <h3>Core Details</h3>
                                        <button type="button" onClick={() => setCurrentModal("profile")} disabled={!selectedMember}>Open Full Profile</button>
                                    </div>
                                    <div className="member-photo-manager">
                                        {renderMemberAvatar(fullName, currentPhotoUrl, "member-photo-preview")}
                                        <div>
                                            <h4>Member Photo</h4>
                                            <p>{selectedMember ? "Add a headshot for faster recognition in the member list and profile overview." : "Save the member first, then add a photo."}</p>
                                            <div className="member-photo-actions">
                                                <input
                                                    ref={photoInputRef}
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handlePhotoSelected}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => photoInputRef.current?.click()}
                                                    disabled={!selectedMember || photoUploading}
                                                >
                                                    {photoUploading ? "Uploading..." : currentPhotoUrl ? "Change Photo" : "Upload Photo"}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleRemovePhoto}
                                                    disabled={!selectedMember || !currentPhotoUrl || photoUploading}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="member-form-grid">
                                        <div className="member-form-group">
                                            <label>Current title</label>
                                            <Select
                                                value={currentTitleOptions.find((option) => option.value === formData.current_title) || null}
                                                onChange={(option) => setFormData((prev) => ({ ...prev, current_title: option?.value || "" }))}
                                                options={currentTitleOptions}
                                                placeholder="Select title"
                                                isClearable
                                            />
                                        </div>
                                        {[
                                            { key: "first_name", label: "First Name" },
                                            { key: "last_name", label: "Last Name" },
                                            { key: "occupation", label: "Occupation" },
                                            { key: "date_of_birth", label: "Date of Birth", type: "date" },
                                            { key: "date_of_death", label: "Date of Death", type: "date" },
                                        ].map(({ key, label, type = "text" }) => (
                                            <div className="member-form-group" key={key}>
                                                <label>{label}</label>
                                                <input type={type} name={key} value={formData[key] || ""} onChange={handleInputChange} className={errors[key] ? "error" : ""} />
                                                {errors[key] && <p className="error-message">{errors[key]}</p>}
                                            </div>
                                        ))}
                                        <div className="member-form-group">
                                            <label>Country</label>
                                            <select name="country_id" value={formData.country_id || ""} onChange={handleInputChange}>
                                                <option value="">Select Country</option>
                                                {countries.map((country) => (
                                                    <option key={country.id} value={country.id}>{country.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="member-form-group wide">
                                            <label>Comments</label>
                                            <textarea name="comments" value={formData.comments || ""} onChange={handleInputChange} rows="5" />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activeTab === "membership" && (
                                <div className="member-tab-panel">
                                    <div className="member-panel-heading">
                                        <h3>Membership History</h3>
                                        <button type="button" onClick={() => setCurrentModal("history")} disabled={!selectedMember}>Manage History</button>
                                    </div>
                                    <div className="member-record-list">
                                        {(memberProfile?.membership_history || []).length === 0 ? (
                                            <p className="member-empty-note">No membership history recorded yet.</p>
                                        ) : memberProfile.membership_history.map((item) => (
                                            <article key={item.id} className={item.is_mother_lodge ? "main-lodge" : ""}>
                                                {editingMembershipId === item.id ? (
                                                    <div className="member-inline-edit">
                                                        <label className="wide member-inline-category-helper">
                                                            Membership Category
                                                            <Select
                                                                value={membershipCategoryOptions.find((option) => option.value === getInlineMembershipCategory()) || null}
                                                                onChange={updateInlineMembershipCategory}
                                                                options={membershipCategoryOptions}
                                                                placeholder="Choose current or former member"
                                                                isClearable
                                                            />
                                                            <span>Tracks whether this lodge membership is current; type, status, and dates stay separate.</span>
                                                        </label>
                                                        <label>
                                                            Lodge
                                                            <Select
                                                                value={lodges
                                                                    .map((lodge) => ({ value: lodge.id, label: lodge.name }))
                                                                    .find((option) => option.value === membershipEditData.lodge_id) || null}
                                                                onChange={(option) => updateMembershipEdit("lodge_id", option?.value || "")}
                                                                options={lodges.map((lodge) => ({ value: lodge.id, label: lodge.name }))}
                                                                placeholder="Select lodge"
                                                                isSearchable
                                                            />
                                                        </label>
                                                        <label className="wide member-inline-checkbox">
                                                            <input
                                                                type="checkbox"
                                                                checked={Boolean(membershipEditData.is_mother_lodge)}
                                                                onChange={(event) => updateMembershipEdit("is_mother_lodge", event.target.checked)}
                                                            />
                                                            {mainBodyLabel(membershipEditData.lodge_id)}
                                                            <span>Use this for the member's primary current body for this lodge type. Selecting it clears the main flag only from memberships in the same stream.</span>
                                                        </label>
                                                        <label>
                                                            Type
                                                            <Select
                                                                value={membershipTypes
                                                                    .map((type) => ({ value: type.id, label: type.name }))
                                                                    .find((option) => option.value === membershipEditData.membership_type_id) || null}
                                                                onChange={(option) => updateMembershipEdit("membership_type_id", option?.value || "")}
                                                                options={membershipTypes.map((type) => ({ value: type.id, label: type.name }))}
                                                                placeholder="Select type"
                                                                isSearchable
                                                            />
                                                        </label>
                                                        <label>
                                                            Status
                                                            <Select
                                                                value={membershipStatuses
                                                                    .map((status) => ({ value: status.id, label: status.name }))
                                                                    .find((option) => option.value === membershipEditData.membership_status_id) || null}
                                                                onChange={(option) => updateMembershipEdit("membership_status_id", option?.value || "")}
                                                                options={membershipStatuses.map((status) => ({ value: status.id, label: status.name }))}
                                                                placeholder="Select status"
                                                                isSearchable
                                                            />
                                                        </label>
                                                        <label>
                                                            Start date
                                                            <input
                                                                type="date"
                                                                value={membershipEditData.start_date || ""}
                                                                onChange={(event) => updateMembershipEdit("start_date", event.target.value)}
                                                            />
                                                        </label>
                                                        <label>
                                                            End date
                                                            <input
                                                                type="date"
                                                                value={membershipEditData.end_date || ""}
                                                                onChange={(event) => updateMembershipEdit("end_date", event.target.value)}
                                                            />
                                                        </label>
                                                        <label className="wide">
                                                            Comments
                                                            <textarea
                                                                rows={2}
                                                                value={membershipEditData.comments || ""}
                                                                onChange={(event) => updateMembershipEdit("comments", event.target.value)}
                                                            />
                                                        </label>
                                                        <div className="member-inline-actions">
                                                            <button type="button" onClick={saveMembershipEdit}>Save</button>
                                                            <button type="button" onClick={cancelMembershipEdit}>Cancel</button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="member-record-heading">
                                                            <strong>{item.lodge_name || "Unknown Lodge"}</strong>
                                                            {item.is_mother_lodge && (
                                                                <span className="member-record-badge">{mainBodyLabel(item.lodge_id)}</span>
                                                            )}
                                                            <button type="button" onClick={() => beginMembershipEdit(item)}>Edit</button>
                                                        </div>
                                                        <span>{item.membership_status || "Status unknown"} · {item.membership_type || "Membership"}</span>
                                                        <p>{formatDate(item.start_date)} - {item.end_date ? formatDate(item.end_date) : "Present"}</p>
                                                    </>
                                                )}
                                            </article>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {activeTab === "degrees" && (
                                <div className="member-tab-panel">
                                    <div className="member-panel-heading">
                                        <h3>Degrees</h3>
                                        <button type="button" onClick={() => setCurrentModal("degrees")} disabled={!selectedMember}>Manage Degrees</button>
                                    </div>
                                    <div className="member-record-list">
                                        {(memberProfile?.degrees || []).length === 0 ? (
                                            <p className="member-empty-note">No degrees recorded yet.</p>
                                        ) : memberProfile.degrees.map((degree) => (
                                            <article key={degree.id}>
                                                <strong>{degree.degree_name || "Unknown Degree"}</strong>
                                                <span>{formatDate(degree.date_obtained)}</span>
                                                {degree.comments && <p>{degree.comments}</p>}
                                            </article>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {activeTab === "officers" && (
                                <div className="member-tab-panel">
                                    <div className="member-panel-heading">
                                        <h3>Officer Roles and Ranks</h3>
                                        <button type="button" onClick={() => setCurrentModal("officers")} disabled={!selectedMember}>Manage Offices</button>
                                    </div>
                                    <div className="member-record-list">
                                        {(memberProfile?.roles || []).length === 0 ? (
                                            <p className="member-empty-note">No officer roles recorded yet.</p>
                                        ) : memberProfile.roles.map((role) => (
                                            <article key={role.id}>
                                                <strong>{role.role_name || "Officer Role"}</strong>
                                                <span>{role.rank_name || "Rank not recorded"} · {role.lodge_name || "Unknown Lodge"}</span>
                                                <p>{formatDate(role.start_date)} - {role.end_date ? formatDate(role.end_date) : "Present"}</p>
                                            </article>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {activeTab === "meetings" && (
                                <div className="member-tab-panel">
                                    <div className="member-panel-heading">
                                        <h3>Meeting Attendance</h3>
                                        <span>{memberProfile?.total_meetings || 0} member · {memberProfile?.total_visits || 0} visitor</span>
                                    </div>
                                    <div className="member-record-list">
                                        {(memberProfile?.meetings || []).length === 0 ? (
                                            <p className="member-empty-note">No meeting attendance recorded yet.</p>
                                        ) : memberProfile.meetings.map((meeting) => (
                                            <article key={meeting.id}>
                                                <strong>{formatDate(meeting.meeting_date)}</strong>
                                                <span>{meeting.attendance_type || "Attendance"} · {meeting.lodge_name || "Unknown Lodge"}</span>
                                                <p>{meeting.eventtype_name || "Meeting"}</p>
                                            </article>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {activeTab === "contact" && (
                                <div className="member-tab-panel">
                                    <h3>Contact and Address</h3>
                                    <div className="member-form-grid">
                                        {[
                                            { key: "phone_number", label: "Phone Number" },
                                            { key: "email_address", label: "Email Address", type: "email" },
                                            { key: "address", label: "Address", wide: true },
                                            { key: "city", label: "City" },
                                            { key: "state", label: "State" },
                                            { key: "postcode", label: "Postcode" },
                                        ].map(({ key, label, type = "text", wide }) => (
                                            <div className={`member-form-group ${wide ? "wide" : ""}`} key={key}>
                                                <label>{label}</label>
                                                <input type={type} name={key} value={formData[key] || ""} onChange={handleInputChange} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {activeTab === "spouse" && (
                                <div className="member-tab-panel">
                                    <h3>Spouse Details</h3>
                                    <div className="member-form-grid">
                                        {[
                                            { key: "spouse_first_name", label: "First Name" },
                                            { key: "spouse_last_name", label: "Last Name" },
                                            { key: "spouse_occupation", label: "Occupation" },
                                            { key: "spouse_date_of_birth", label: "Date of Birth", type: "date" },
                                            { key: "spouse_date_of_death", label: "Date of Death", type: "date" },
                                            { key: "spouse_phone_number", label: "Phone Number" },
                                            { key: "spouse_email_address", label: "Email Address", type: "email" },
                                            { key: "spouse_comments", label: "Notes", wide: true, multiline: true },
                                        ].map(({ key, label, type = "text", wide, multiline }) => (
                                            <div className={`member-form-group ${wide ? "wide" : ""}`} key={key}>
                                                <label>{label}</label>
                                                {multiline ? (
                                                    <textarea name={key} value={formData[key] || ""} onChange={handleInputChange} rows="5" />
                                                ) : (
                                                    <input type={type} name={key} value={formData[key] || ""} onChange={handleInputChange} />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </section>
                    </div>
                )}

                {(selectedMember || isAddingNew) && (
                    <div className="member-modal-actions">
                        <button className="member-save-btn" onClick={handleSave}>Save Member</button>
                        <button className="member-delete-btn" onClick={handleDelete} disabled={!selectedMember}>Delete</button>
                        <button className="member-cancel-btn" onClick={onClose}>Cancel</button>
                    </div>
                )}
            </div>

            {currentModal === "history" && <MembershipHistoryModal memberId={selectedMember?.id} onClose={closeChildModal} />}
            {currentModal === "degrees" && <MembershipDegreesModal memberId={selectedMember?.id} onClose={closeChildModal} />}
            {currentModal === "officers" && <OfficerRoleModal memberId={selectedMember?.id} onClose={closeChildModal} />}
            {currentModal === "profile" && <MemberProfileModal memberId={selectedMember?.id} onClose={closeChildModal} />}
            {showLodgeRollView && (
                <div className="member-lodge-roll-overlay">
                    <section className="member-lodge-roll-modal">
                        <div className="member-lodge-roll-header">
                            <div>
                                <p className="member-eyebrow">Secretary List</p>
                                <h3>Names, Addresses and Telephone Numbers</h3>
                                <span>{getMemberFilterLabel()}</span>
                            </div>
                            <div>
                                <button type="button" onClick={downloadLodgeRollPdf}>Download PDF</button>
                                <button type="button" onClick={() => setShowLodgeRollView(false)}>Close</button>
                            </div>
                        </div>
                        <div className="member-lodge-roll-table-wrap">
                            <table className="member-lodge-roll-table">
                                <colgroup>
                                    <col className="roll-col-number" />
                                    <col className="roll-col-name" />
                                    <col className="roll-col-grade" />
                                    <col className="roll-col-address" />
                                    <col className="roll-col-email" />
                                    <col className="roll-col-phone" />
                                    <col className="roll-col-vocation" />
                                    <col className="roll-col-birthday" />
                                    <col className="roll-col-partner" />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>No.</th>
                                        <th>Name</th>
                                        <th>Grade</th>
                                        <th>Address</th>
                                        <th>E-mail</th>
                                        <th>Phone / Mobile</th>
                                        <th>Vocation</th>
                                        <th>Birthday</th>
                                        <th>Partner / Birthday</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lodgeRollRows.map((member, index) => (
                                        <tr key={member.id}>
                                            <td data-label="No.">{index + 1}</td>
                                            <td data-label="Name">{member.name}</td>
                                            <td data-label="Grade">{member.grade || "Not recorded"}</td>
                                            <td data-label="Address">{member.address || "Not recorded"}</td>
                                            <td data-label="E-mail">{member.email || "Not recorded"}</td>
                                            <td data-label="Phone / Mobile">{member.phone || "Not recorded"}</td>
                                            <td data-label="Vocation">{member.vocation || "Not recorded"}</td>
                                            <td data-label="Birthday">{member.birthday || "Not recorded"}</td>
                                            <td data-label="Partner / Birthday">{member.partner || "Not recorded"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
}

export default MemberDetailsModal;
